import { useState } from 'react';
import { API_URL, API_VERSION, SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

const ENDPOINT_URL = '/srv-mu-services/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetPointsServiceDetailInput = paths[url]['get']['parameters']['path'];

export interface GetPointsServiceDetailSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get a points service.
 */
export const useGetPointsServiceDetail = () => {
    const { navigate } = useCustomNavigate();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setPointsServiceDetail } = useData();
    const [loading, setLoading] = useState<boolean>(false);

    const request = async (props: GetPointsServiceDetailInput) => {
        setLoading(true);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();

            const url = `${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid || '')}?${inputParameters}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetPointsServiceDetailSuccessResponse = await response.json();

            setPointsServiceDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);

            if (err.statusCode === 404) {
                // redirect to 404 page
                navigate(`/${SITE_URLS.pageNotFound}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return { request, loading };
};
